import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BuddyOverdue, useBuddyOverdues, useRealtime } from "../contexts/RealtimeContext";
import { postReq } from "../utils/request";

const VISIBLE_ROWS = 3;

/**
 * Soft, non-blocking amber stripe shown at the top of the app shell when
 * one or more operators have missed a buddy-check (dead-man timer).
 * Sibling to `<RealtimePanicOverlay />`, but unlike panic this never
 * sounds, never blocks, and the dispatcher can ignore it. Up to three
 * rows are visible at once; the rest collapse behind a "+N more" toggle.
 *
 * Each row exposes:
 *   - Acknowledge — POST /mobile/buddy-check-overdue/:id/acknowledge,
 *     then optimistic local remove via the realtime context.
 *   - Open mission — only when the operator's last known itinerary
 *     resolves to a mission.
 */
const BuddyOverdueBanner: React.FC = () => {
    const overdues = useBuddyOverdues();
    const { acknowledgeBuddyOverdue } = useRealtime();
    const navigate = useNavigate();
    const [showAll, setShowAll] = useState(false);
    const [pendingAck, setPendingAck] = useState<Set<number>>(new Set());

    if (overdues.length === 0) return null;

    const visible = showAll ? overdues : overdues.slice(0, VISIBLE_ROWS);
    const hiddenCount = overdues.length - visible.length;

    const handleAcknowledge = async (row: BuddyOverdue) => {
        const id = row.buddyCheckOverdueId;
        if (pendingAck.has(id)) return;
        setPendingAck((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });
        const response = await postReq(`missions/buddy-check-overdue/${id}/acknowledge`, {});
        if (response.status === 200) {
            acknowledgeBuddyOverdue(id);
        } else {
            toast.error("Could not acknowledge buddy check");
            setPendingAck((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const handleOpenMission = (row: BuddyOverdue) => {
        if (!row.operator.missionId) return;
        navigate(`/mission/${row.operator.missionId}`);
    };

    return (
        <div
            style={{
                position: "sticky",
                top: 0,
                zIndex: 1040,
                backgroundColor: "#7a4f00",
                color: "#fff8e6",
                borderBottom: "2px solid var(--status-warning-solid)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            }}
        >
            {visible.map((row) => {
                const ageMin = Math.max(0, Math.round((Date.now() - new Date(row.triggeredAt).getTime()) / 60000));
                const initials = row.operator.name
                    .split(" ")
                    .slice(0, 2)
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase() || "?";
                const isPending = pendingAck.has(row.buddyCheckOverdueId);
                return (
                    <div
                        key={row.buddyCheckOverdueId}
                        className="d-flex align-items-center px-3 py-2 gap-3"
                        style={{ borderTop: "1px solid rgba(255,184,74,0.3)" }}
                    >
                        <i className="bi bi-shield-exclamation" style={{ fontSize: "var(--fs-xl)", color: "var(--status-warning-solid)" }} />
                        {row.operator.photoUrl ? (
                            <img
                                src={row.operator.photoUrl}
                                alt={row.operator.name}
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    border: "1px solid var(--status-warning-solid)",
                                    objectFit: "cover",
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    backgroundColor: "var(--status-warning-bg)",
                                    color: "var(--status-warning-fg)",
                                    fontWeight: 700,
                                    fontSize: "var(--fs-xs)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {initials}
                            </div>
                        )}
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: "var(--fs-md)" }}>{row.operator.name}</div>
                            <div style={{ fontSize: "var(--fs-xs)", opacity: 0.85 }}>
                                Missed buddy check {ageMin}m ago
                                {row.intervalMinutes ? ` (${row.intervalMinutes}m interval)` : ""}
                            </div>
                        </div>
                        {row.operator.missionId ? (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-light"
                                onClick={() => handleOpenMission(row)}
                            >
                                Open mission
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={() => handleAcknowledge(row)}
                            disabled={isPending}
                        >
                            {isPending ? "Acknowledging…" : "Acknowledge"}
                        </button>
                    </div>
                );
            })}
            {hiddenCount > 0 ? (
                <div className="d-flex justify-content-center py-1" style={{ borderTop: "1px solid rgba(255,184,74,0.3)" }}>
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-warning"
                        onClick={() => setShowAll(true)}
                        style={{ textDecoration: "none" }}
                    >
                        +{hiddenCount} more — show all
                    </button>
                </div>
            ) : null}
            {showAll && overdues.length > VISIBLE_ROWS ? (
                <div className="d-flex justify-content-center py-1" style={{ borderTop: "1px solid rgba(255,184,74,0.3)" }}>
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-warning"
                        onClick={() => setShowAll(false)}
                        style={{ textDecoration: "none" }}
                    >
                        Collapse
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export default BuddyOverdueBanner;
